import { useCallback, useRef, useState } from "react";
import { useConfig } from "./useConfig";

type DownloadState = {
  isDownloading: boolean;
  error: string | null;
};

export const useFileDownload = () => {
  const [state, setState] = useState<DownloadState>({
    isDownloading: false,
    error: null,
  });

  const { config } = useConfig();
  const abortControllerRef = useRef<AbortController | null>(null);

  const download = useCallback(
    async (sessionId: string, fileName: string) => {
      if (state.isDownloading) return;

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setState({ isDownloading: true, error: null });

      try {
        const response = await fetch(
          `${config.apiUrl}/lottery/results/${sessionId}/pdf`,
          {
            method: "GET",
            signal: abortControllerRef.current.signal,
            headers: { Accept: "*/*" },
          }
        );

        if (!response.ok) {
          throw new Error(`Download failed (${response.status})`);
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        setState({ isDownloading: false, error: null });
      } catch (err: any) {
        if (err.name === "AbortError") return;

        setState({
          isDownloading: false,
          error: err.message ?? "Download failed",
        });
      }
    },
    [config.apiUrl, state.isDownloading]
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setState({ isDownloading: false, error: null });
  }, []);

  return {
    download,
    cancel,
    ...state,
  };
};
